import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SecurityContext,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AngularEditorComponent, AngularEditorConfig, AngularEditorService } from '@kolkov/angular-editor';
import { StringHelper } from '../../helpers/string.helper';
import { EmojiData } from '../../models/emoji.model';

@Component({
  selector: 'app-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss']
})
export class RichTextEditorComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() maxLength: number = 0;
  @Input() defaultText: string = '';
  @Input() placeholderKey: string = '';
  @Input() disabled = false;
  @Input() contentMode: 'legacy' | 'structured' = 'legacy';

  public editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '150',
    maxHeight: 'auto',
    width: 'auto',
    minWidth: '0',
    translate: 'yes',
    defaultParagraphSeparator: '',
    defaultFontName: 'Nunito',
    defaultFontSize: '',
    sanitize: false,
    toolbarPosition: 'top',
    toolbarHiddenButtons: [
      ['outdent', 'indent', 'backgroundColor', 'fontName'],
      ['insertImage', 'insertVideo', 'insertHorizontalRule', 'toggleEditorMode', 'link', 'unlink']
    ]
  };

  public emptyStringError: boolean = false;
  public get hasValidationError(): boolean {
    return this.emptyStringError || this.rawText.length > this.maxLength;
  }

  public get text(): string {
    return this._text;
  }
  public get rawText(): string {
    return this._rawText;
  }
  private set text(value: string) {
    if (this.contentMode === 'structured') {
      this._text = value ?? '';
      this._rawText = StringHelper.getRichTextVisibleText(this._text, 'structured');
      return;
    }

    const temp = StringHelper.replaceNewLines(value ?? '');
    this._text = StringHelper.removeAdditionalTags(temp);
    this._rawText = StringHelper.processValueToEntity(this._text);
  }

  public readonly fonts: string[] = ['Arial', 'Times New Roman', 'Calibri', 'Comic Sans MS', 'Nunito'];
  public fontName: string = 'Nunito';

  private _text: string = '';
  private _rawText: string = '';
  private removePasteListener: (() => void) | null = null;

  @ViewChild('editor') private angularEditor!: AngularEditorComponent;

  constructor(
    private sanitizer: DomSanitizer,
    private editorService: AngularEditorService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.text = this.defaultText;
  }

  ngAfterViewInit(): void {
    this.updatePasteListener();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      this.editorConfig.editable = !changes.disabled.currentValue;
    }
    if (changes.contentMode && !changes.contentMode.firstChange) {
      this.text = this.defaultText ?? '';
      this.updatePasteListener();
    }
    if (changes.defaultText && !changes.defaultText.firstChange) {
      this.text = changes.defaultText.currentValue ?? '';
      this.emptyStringError = !this.hasTextContent;
    }
  }

  public onTextChanged(value: string) {
    this.text = value;
    this.emptyStringError = !this.hasTextContent;
  }

  /**
   * ! NOT USED IN COMPONENT, BUT IN PARENTS
   * @return text with HTML markups
   */
  public onSubmit(): string | null {
    if (this.contentMode === 'structured' && !this.dirty) {
      if (!this.hasTextContent || this.rawText.length > this.maxLength) {
        this.emptyStringError = !this.hasTextContent;
        return null;
      }

      return this.defaultText;
    }

    let tempText: string = this.text;
    // HACK: Ultra hack because Chromium (or text editor) is stupid
    // eslint-disable-next-line no-useless-escape
    tempText = tempText.replace(/style=\"text-align: right;\"/g, 'align="right"');
    // eslint-disable-next-line no-useless-escape
    tempText = tempText.replace(/style=\"text-align: center;\"/g, 'align="center"');
    // eslint-disable-next-line no-useless-escape
    tempText = tempText.replace(/style=\"text-align: left;\"/g, 'align="left"');
    tempText = this.sanitizer.sanitize(SecurityContext.HTML, tempText) ?? '';
    this.text = StringHelper.processValueToEntity(tempText);

    if (!this.hasTextContent || this.rawText.length > this.maxLength) {
      this.emptyStringError = !this.hasTextContent;
      return null;
    } else {
      return this.text;
    }
  }

  // USED IN PARENT COMPONENT
  public get dirty(): boolean {
    return this.defaultText !== this.text;
  }

  public openSelectFont(event: Event): void {
    this.preventFocus(event);
  }

  public onSelectFont(font: string, event: Event): void {
    this.preventFocus(event);
    this.fontName = font;
    this.editorService.executeCommand('fontName', font);
  }

  public insertEmoji(emoji: EmojiData): void {
    if (this.angularEditor.focused) this.editorService.insertHtml(emoji.html);
  }

  ngOnDestroy(): void {
    this.removePasteListener?.();
  }

  private updatePasteListener(): void {
    this.removePasteListener?.();
    this.removePasteListener = null;

    if (this.contentMode !== 'structured' || !this.angularEditor?.textArea?.nativeElement) return;

    this.removePasteListener = this.renderer.listen(this.angularEditor.textArea.nativeElement, 'paste', (event: ClipboardEvent) =>
      this.onStructuredPaste(event)
    );
  }

  private onStructuredPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const htmlData = event.clipboardData?.getData('text/html') ?? '';
    const textData = event.clipboardData?.getData('text/plain') ?? '';
    const preparedHtml = this.prepareStructuredPasteValue(htmlData, textData);
    if (!preparedHtml.length) return;

    this.angularEditor.focus();
    this.editorService.insertHtml(preparedHtml);
    // `insertHtml()` updates the DOM only; we need the editor's internal sync step so Angular picks up the new model value.
    this.angularEditor.onContentChange(this.angularEditor.textArea.nativeElement);
  }

  private get hasTextContent(): boolean {
    if (this.contentMode === 'structured') {
      return StringHelper.hasVisibleRichText(this.text, 'structured');
    }

    return this.rawText.length > 0;
  }

  private prepareStructuredPasteValue(htmlValue: string, textValue: string): string {
    const preparedValue = htmlValue.length
      ? StringHelper.prepareClipboardHtml(htmlValue)
      : StringHelper.prepareClipboardText(textValue);
    return this.sanitizer.sanitize(SecurityContext.HTML, preparedValue) ?? '';
  }

  private preventFocus(event: Event): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();
  }
}
